import { create } from 'apisauce';
import { CamelcaseSerializer, SnakecaseSerializer } from 'cerealizr';

import { SNAKE_DELIVERY_TYPES } from '~constants/delivery';

import { errorNormalizer } from './apiTransforms';

const camelSerializer = new CamelcaseSerializer();
const snakeSerializer = new SnakecaseSerializer();

export const HEADERS = {
  PLATFORM: 'X-PLATFORM',
  TENANT: 'x-tenant',
  CREATE_USER_TOKEN: 'X-CREATE-USER-TOKEN',
  AUTHORIZATION: 'Authorization',
  JOB_AUTHORIZATION: 'X-JOB-AUTHORIZATION',
  USER_ID: 'X-USERID',
  ADMIN_STORE_ID: 'X-ADMINSTOREID'
};

export const setTenantForCDN = () => {
  if (process.env.NODE_ENV === 'test') {
    return '';
  }

  const req = new XMLHttpRequest();
  req.open('GET', document.location, false);
  req.send(null);
  return req.getResponseHeader('tenant') || 'justburger';
};

// axios defaults to no timeout, so a hung request never settles.
export const REQUEST_TIMEOUT_MS = 30000;

// Nothing is idempotency-keyed, so aborting a mutation the server applied invites a duplicate.
export const MUTATION_TIMEOUT_MS = 120000;

const MUTATION_METHODS = ['post', 'put', 'patch', 'delete'];

export const timeoutForMethod = (method) =>
  MUTATION_METHODS.includes(String(method).toLowerCase()) ? MUTATION_TIMEOUT_MS : REQUEST_TIMEOUT_MS;

export const api = create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    [HEADERS.PLATFORM]: 'web'
  }
});

export const apiAry = create({
  baseURL: process.env.REACT_APP_CORE_HOST
});

export const cdnApi = create({
  baseURL: `${process.env.REACT_APP_PUBLIC_CDN}${setTenantForCDN()}`
});

export const setTokenHeader = (token) => {
  api.setHeaders({
    Authorization: `${token}`
  });
};

export const setTenant = (tenant) => {
  api.setHeaders({
    [HEADERS.TENANT]: tenant
  });
};

export const apiSetup = (baseApi) => {
  // Here, not at each create(): services build their own instances that all pass through apiSetup.
  baseApi.axiosInstance.defaults.timeout = REQUEST_TIMEOUT_MS;

  baseApi.addRequestTransform((request) => {
    request.timeout = timeoutForMethod(request.method);
  });

  baseApi.addResponseTransform(errorNormalizer);

  baseApi.addResponseTransform((response) => {
    if (response.data) {
      response.data = camelSerializer.serialize(response.data);
    }
  });

  baseApi.addRequestTransform((request) => {
    if (request.params) {
      request.params = snakeSerializer.serialize(request.params);
    }
    if (request.data) {
      request.data = snakeSerializer.serialize(request.data);
      if (request?.data?.store?.delivery_methods_attributes) {
        request.data.store.delivery_methods_attributes =
          request.data.store.delivery_methods_attributes.filter(
            (deliveryMethod) => deliveryMethod.delivery_type !== 'uber_delivery'
          );
      }
      const targetDeliveryMethod = request?.data?.store?.delivery_methods_attributes?.find(
        (deliveryMethod) => deliveryMethod.delivery_type === SNAKE_DELIVERY_TYPES.PedidosYaV3Delivery
      );

      if (targetDeliveryMethod && targetDeliveryMethod.external_delivery_cofinance) {
        const modifiedCofinance = {
          [SNAKE_DELIVERY_TYPES.PedidosYaV3Delivery]:
            targetDeliveryMethod.external_delivery_cofinance.peya_v_3_delivery
        };

        request.data = {
          ...request.data,
          store: {
            ...request.data.store,
            delivery_methods_attributes: request.data.store.delivery_methods_attributes.map(
              (deliveryMethod) =>
                deliveryMethod.delivery_type === SNAKE_DELIVERY_TYPES.PedidosYaV3Delivery
                  ? {
                      ...deliveryMethod,
                      external_delivery_cofinance: modifiedCofinance
                    }
                  : deliveryMethod
            )
          }
        };
      }
    }
  });
};

export const getTenantId = (tenant) => process.env[`REACT_APP_FIREBASE_ID_${tenant.toUpperCase()}`];

apiSetup(api);
apiSetup(apiAry);
apiSetup(cdnApi);

export default api;
